import React, { ChangeEvent, useState, FormEvent, useEffect } from 'react';
import NoticeComponent from '../../components/NoticeComponent';
import ProfileSidebar from "../../components/ProfileSidebar";
import { instance } from '../../api/axiosInstance'; // Axios 인스턴스 임포트
import { toast, ToastContainer } from 'react-toastify'; // 사용자 알림을 위한 라이브러리
import Cookies from 'js-cookie';

import 'react-toastify/dist/ReactToastify.css';
import { snsTypes } from "./SnsType";
import { UserSnsResponseDTO } from "./UserSnsResponseDTO";
import { SnsLink } from "./SnsLink";

const getSnsTypeNameById = (typeId: number): string => {
  const type = snsTypes.find(t => t.id === typeId);
  return type ? type.name : '';
};

export default function SnsAdd() {
  const [snsLinks, setSnsLinks] = useState<SnsLink[]>(snsTypes.map(type => ({ type: type.id, url: '' })));
  const [isToggled, setIsToggled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchSnsLinks = async () => {
      setIsLoading(true);
      try {
        const accessToken = Cookies.get('access_token');
        if (!accessToken) {
          throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
        }

        const response = await instance.get('/auth/sns', {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        const fetchedLinks: UserSnsResponseDTO[] = response.data;

        setSnsLinks(prevLinks => {
          return prevLinks.map(link => {
            const typeName = getSnsTypeNameById(link.type);
            const existingLink = fetchedLinks.find(fLink => fLink.snsType === typeName);
            return existingLink
              ? { ...link, url: existingLink.snsAddress, id: existingLink.id }
              : link;
          });
        });
      } catch (error: any) {
        console.error("SNS 링크를 가져오는 데 실패했습니다:", error);
        const errorMessage = error.response?.data?.message || "SNS 링크를 가져오는 데 실패했습니다.";
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSnsLinks();
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setIsToggled(user.snsPublic || false);
      } catch (e) {
        console.error('로컬 스토리지에서 사용자 데이터를 파싱하는 데 실패했습니다:', e);
      }
    }
  }, []);

  const handleInputChange = (typeId: number, url: string) => {
    setSnsLinks(prevLinks =>
      prevLinks.map(link =>
        link.type === typeId ? { ...link, url } : link
      )
    );
  };

  const handleDelete = async (snsId: number) => {
    if (!window.confirm("정말로 이 SNS 링크를 삭제하시겠습니까?")) return;

    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
      }

      // SNS 링크 삭제 요청
      await instance.delete(`/auth/sns/${snsId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      // 삭제한 링크의 URL을 빈 문자열로 설정
      setSnsLinks(prevLinks =>
        prevLinks.map(link => link.id === snsId ? { ...link, url: '' } : link)
      );

      toast.success("SNS 링크가 성공적으로 삭제되었습니다.");
    } catch (error: any) {
      console.error("SNS 링크 삭제에 실패했습니다:", error);
      const errorMessage = error.response?.data?.message || "SNS 링크 삭제에 실패했습니다.";
      toast.error(errorMessage);
    }
  };


  const saveLinks = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('인증 토큰이 없습니다. 다시 로그인해주세요.');
      }

      const requests = snsLinks.map(async (link) => {
        const snsTypeObj = snsTypes.find(t => t.id === link.type);
        if (!snsTypeObj) return null;

        const payload = {
          snsType: snsTypeObj.name,
          snsAddress: link.url
        };

        // URL이 비어있다면 삭제
        if (link.url.trim() === '') {
          if (link.id) {
            await instance.delete(`/auth/sns/${link.id}`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
              },
            });
            return null;
          }
          return null;
        }

        if (link.id) {
          // 기존 링크가 있다면 업데이트
          return instance.put(`/auth/sns/${link.id}`, payload, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            },
          });
        } else {
          // 기존 링크가 없다면 새로 생성
          return instance.post('/auth/sns', payload, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            },
          });
        }
      });

      await Promise.all(requests);

      const newToggle = !isToggled;
      setIsToggled(!newToggle);

      await instance.put('/auth/users/sns-public', {
        isSnsPublic: newToggle
      }, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success("설정이 저장되었습니다.");

      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        user.snsPublic = newToggle;
        localStorage.setItem('user', JSON.stringify(user));
      }

    } catch (error: any) {
      console.error("SNS 링크 저장에 실패했습니다:", error);
      const errorMessage = error.response?.data?.message || "SNS 링크 저장에 실패했습니다.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <section className="mt-[90px] max-w-[1440px] w-full mx-auto min-h-screen">
      <ToastContainer />
      <div className="flex">
        <ProfileSidebar />
        <article className="flex-1 px-[80px] pt-[48px] pb-[80px] max-w-[1440px] w-full">
          <NoticeComponent title="SNS 주소" content="공개 가능한 SNS 주소 겸 연락처" />
          {isLoading ? (
            <div className="flex justify-center items-center mt-8">
              <div className="loader"></div>
            </div>
          ) : (
            <form onSubmit={saveLinks} className="mt-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {snsLinks.map(link => {
                  const snsType = snsTypes.find(type => type.id === link.type);
                  return (
                    <div key={link.type} className="flex items-center">
                      <div className="flex-shrink-0">
                        {snsType?.icon}
                      </div>
                      <input
                        type="url"
                        placeholder={`https://${snsType?.name.toLowerCase()}.com/`}
                        value={link.url}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange(link.type, e.target.value)}
                        className="ml-4 max-w-[270px] flex-1 px-4 py-2 h-[43px] rounded-md bg-gray-100 text-gray-800 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        aria-label={`${snsType?.name} URL`}
                      />
                      {link.id && link.url.trim() !== '' && (
                        <button
                          type="button"
                          onClick={() => handleDelete(link.id!)}
                          className="ml-2 text-red-500 hover:text-red-700"
                          aria-label={`Delete ${snsType?.name} URL`}
                        >
                          삭제
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center pt-[38px] pb-[30px]">
                <input
                  type="checkbox"
                  checked={isToggled}
                  onChange={() => setIsToggled(!isToggled)} // UI 업데이트
                  id="privacyToggle"
                  className="hidden"
                />
                <label htmlFor="privacyToggle" className="flex items-center cursor-pointer">
                  <div
                    className={`w-[34px] h-[18px] flex items-center shadow-sm rounded-full p-1 transition-colors duration-300 ease-in-out ${isToggled ? 'bg-MainColor' : 'bg-gray-500'}`}
                  >
                    <div
                      className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ease-in-out ${isToggled ? 'translate-x-[14px]' : 'translate-x-[-3px]'}`}
                    ></div>
                  </div>
                  <span className="ml-3 text-MainTextColor font-sm leading-4">
                    다른 사용자가 내 프로필에서 이 정보를 볼 수 있도록
                    허용 - 스팸, 피싱 및 기타 위협에 노출될 수 있음을
                    이해하고 이에 동의합니다.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                className="w-full py-4 mt-6 h-[55px] bg-black text-white font-bold text-base tracking-wide rounded-lg hover:bg-opacity-70 transition duration-300"
                disabled={isLoading} // 로딩 중 버튼 비활성화
              >
                {isLoading ? '저장 중...' : '저장'}
              </button>
            </form>
          )}
        </article>
      </div>
    </section>
  );
}
